<template>
    <div class="bg-neutral-100 dark:bg-neutral-800 rounded my-4 overflow-hidden">

        <div class="dark:bg-neutral-700 bg-neutral-200 flex items-center justify-between p-3">

            <div>
                <span class="font-semibold">{{ workout.workoutAt.format('ddd MMM D, YYYY') }}</span>
                <div class="text-sm flex items-center gap-x-4">
                    <div v-if="user.startDate">{{ workout.sinceStart(user.startDate) }}</div>
                    <div v-if="workout.duration">
                        <Icon icon="stopwatch" /> {{$timerValue(workout.duration)}}
                    </div>
                </div>
            </div>

            <div class="flex items-center justify-between gap-x-5">
                <div v-if="loading">
                    <Icon icon="loader" class="text-lg fa-spin" />
                </div>
                <div class="cursor-pointer" @click="handleWorkoutDuplicate" v-else>
                    <Icon icon="copy" class="text-lg"></Icon>
                </div>
                <div class="cursor-pointer" @click="handleWorkoutShare">
                    <Icon icon="arrow-up-from-bracket" class="text-lg"></Icon>
                </div>
                <div class="cursor-pointer" data-cy="edit-workout" @click="handleWorkoutEdit">
                    <Icon icon="edit" class="text-lg"></Icon>
                </div>
            </div>

        </div>

        <div v-if="workout.file">
            <img :src="workout.file.proxyUrl" :alt="workout.file.name">
        </div>

        <div v-if="workout.template" class="bg-neutral-400 dark:bg-neutral-900 p-1 text-center font-bold">
            {{workout.template.name}}
        </div>

        <div class="p-3 text-center font-semibold" v-if="!workout.exercises.length">
            No Exercises Logged
        </div>

        <div class="-my-1.5" v-else-if="!exerciseSetsEnabled">
            <div class="p-3">
                <template v-for="exercise in workout.exercises" :key="exercise.id || exercise.rank">
                    <ExerciseRow :exercise="exercise" />
                </template>
            </div>
        </div>

        <div class="p-3" v-else>
            <div v-for="group in exerciseGroups"
                 :key="group.key"
                 class="my-2 overflow-hidden rounded border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-white/[2%]"
                 data-cy="workout-exercise-set-group">
                <div class="flex items-center justify-between gap-3 border-b border-neutral-200 px-3 py-2 dark:border-neutral-700">
                    <div class="min-w-0">
                        <div class="truncate font-semibold">{{ movementName(group.exercises[0]) }}</div>
                    </div>
                </div>

                <div v-for="(exercise, index) in group.exercises"
                     :key="exercise.id || `${group.key}-${index}`"
                     class="flex items-stretch border-t border-neutral-200 bg-neutral-50 first:border-t-0 dark:border-neutral-700 dark:bg-white/[2%]">
                    <div class="flex w-16 shrink-0 items-center justify-center border-r border-neutral-200 text-xs font-semibold text-neutral-600 dark:border-neutral-700 dark:text-neutral-400"
                         data-cy="workout-exercise-set-label">
                        Set {{ index + 1 }}
                    </div>
                    <ExerciseRow class="min-w-0 flex-1"
                                 :exercise="exercise"
                                 :bordered="false"
                                 :compact="true"
                                 :show-movement-name="false"
                    />
                </div>
            </div>
        </div>

        <div class="p-4 pt-0" v-if="workout.notes">
            <Label>Notes</Label>
            <div class="text-sm mt-3 bg-neutral-200 dark:bg-neutral-900 px-3 py-2 rounded whitespace-pre-line">{{ workout.notes }}</div>
        </div>

    </div>
</template>

<script>
import {defineComponent} from 'vue'

import ExerciseRow from '@/components/Exercise/Row.vue';
import {mapActions} from 'vuex';
import {settingIsEnabled} from '@/utils/settings.js';

export default defineComponent({
  name: 'ResourceListItem',
  components: {
    ExerciseRow,
  },
  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    exerciseGroups() {
      return this.workout.exercises.map((exercise, index) => ({
        exercise,
        index,
      })).reduce((groups, item) => {
        const existingGroup = groups.find(group => group.key === this.exerciseGroupKey(item.exercise, item.index));

        if (existingGroup) {
          existingGroup.exercises.push(item.exercise);
          return groups;
        }

        groups.push({
          key: this.exerciseGroupKey(item.exercise, item.index),
          exercises: [item.exercise],
        });

        return groups;
      }, []);
    },
    exerciseSetsEnabled() {
      return settingIsEnabled(this.user?.settings, 'exerciseSetsEnabled', 'exercise_sets_enabled', true);
    },
    workout() {
      return this.resource;
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  methods: {
    ...mapActions({
      workoutDuplicate: 'workout/duplicate',
    }),
    handleWorkoutDuplicate() {
        this.loading = true;
        return this.workoutDuplicate({
          id: this.workout.uuid,
        }).then((id) => {
          window.app.snackbar({
            message: 'Workout Duplicated',
          });
          const workout = this.$store.getters['workout/findBy'](id);
          this.$router.push(`/workout/${workout.uuid}/edit`);
        }).finally(() => {
          this.loading = false;
        });
    },
    handleWorkoutEdit() {
      this.$router.push(`/workout/${this.workout.uuid}/edit`);
    },
    handleWorkoutShare() {
      this.$router.push(`/workout/${this.workout.uuid}/share`);
    },
    exerciseGroupKey(exercise, index) {
      return exercise.setId ? `set:${exercise.setId}` : `exercise:${exercise.id || index}`;
    },
    movementName(exercise) {
      return exercise.movement ? exercise.movement.name : 'Movement';
    },
  },
  props: {
    resource: {
      required: true,
    },
  },
})
</script>

<style scoped lang="scss">

</style>
